import { createReactBlockSpec } from "@blocknote/react";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { DEFAULT_LINK_PREVIEW_IMAGE } from "./defaultLogo";

export interface LinkMetadata {
  url: string;
  title: string;
  description?: string;
  image?: string;
  domain: string;
}

const metadataCache = new Map<string, LinkMetadata>();

export async function fetchLinkMetadata(
  url: string,
  apiEndpoint: string
): Promise<LinkMetadata> {
  const cached = metadataCache.get(url);
  if (cached) return cached;

  const response = await fetch(
    `${apiEndpoint}?url=${encodeURIComponent(url)}`
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch metadata: ${response.status}`);
  }

  const metadata: LinkMetadata = await response.json();
  metadataCache.set(url, metadata);
  return metadata;
}

export function clearMetadataCache() {
  metadataCache.clear();
}

function extractDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

const LinkPreviewCard = ({
  url,
  title,
  description,
  image,
  domain,
  editable,
  onDelete,
  width,
  onWidthChange,
  height,
  onHeightChange,
}: {
  url: string;
  title: string;
  description: string;
  image: string;
  domain: string;
  editable: boolean;
  onDelete?: () => void;
  width?: number;
  onWidthChange?: (width: number) => void;
  height?: number;
  onHeightChange?: (height: number) => void;
}) => {
  const [imgError, setImgError] = useState(false);
  const [hovered, setHovered] = useState(false);

  type ResizeParams = {
    handleUsed: "left" | "right" | "bottom";
    initialClientX: number;
    initialClientY: number;
    initialWidth: number;
    initialHeight: number;
  };
  const [resizeParams, setResizeParams] = useState<ResizeParams | undefined>(undefined);
  const [localWidth, setLocalWidth] = useState<number | undefined>(width);
  const [localHeight, setLocalHeight] = useState<number | undefined>(height);
  const cardRef = useRef<HTMLDivElement>(null);
  const justResizedRef = useRef(false);

  useEffect(() => { setLocalWidth(width); }, [width]);
  useEffect(() => { setLocalHeight(height); }, [height]);

  useEffect(() => {
    if (!resizeParams) return;

    const onMouseMove = (e: MouseEvent) => {
      if (resizeParams.handleUsed === "bottom") {
        const delta = e.clientY - resizeParams.initialClientY;
        setLocalHeight(Math.min(Math.max(resizeParams.initialHeight + delta, 100), 600));
      } else {
        const delta = resizeParams.handleUsed === "left"
          ? resizeParams.initialClientX - e.clientX
          : e.clientX - resizeParams.initialClientX;
        setLocalWidth(Math.min(
          Math.max(resizeParams.initialWidth + delta, 200),
          cardRef.current?.parentElement?.clientWidth || 800
        ));
      }
    };

    const onMouseUp = () => {
      const handle = resizeParams.handleUsed;
      setResizeParams(undefined);
      justResizedRef.current = true;
      setTimeout(() => { justResizedRef.current = false; }, 50);
      if (handle === "bottom") {
        if (localHeight && onHeightChange) onHeightChange(localHeight);
      } else {
        if (localWidth && onWidthChange) onWidthChange(localWidth);
      }
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [resizeParams, localWidth, localHeight, onWidthChange, onHeightChange]);

  const handleLeftDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setResizeParams({
      handleUsed: "left",
      initialWidth: cardRef.current!.clientWidth,
      initialHeight: localHeight || 200,
      initialClientX: e.clientX,
      initialClientY: e.clientY,
    });
  }, [localHeight]);

  const handleRightDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setResizeParams({
      handleUsed: "right",
      initialWidth: cardRef.current!.clientWidth,
      initialHeight: localHeight || 200,
      initialClientX: e.clientX,
      initialClientY: e.clientY,
    });
  }, [localHeight]);

  const handleBottomDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setResizeParams({
      handleUsed: "bottom",
      initialWidth: cardRef.current!.clientWidth,
      initialHeight: localHeight || 200,
      initialClientX: e.clientX,
      initialClientY: e.clientY,
    });
  }, [localHeight]);

  const handleClick = useCallback(() => {
    if (url && !resizeParams && !justResizedRef.current) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, [url, resizeParams]);

  const resizeCursor = resizeParams
    ? resizeParams.handleUsed === "bottom" ? "ns-resize" : "ew-resize"
    : "pointer";

  return (
    <div
      ref={cardRef}
      className="lumir-link-preview-card"
      onClick={handleClick}
      style={{
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        overflow: "hidden",
        cursor: resizeCursor,
        width: localWidth ? `${localWidth}px` : undefined,
        maxWidth: "100%",
        backgroundColor: "#fff",
        transition: resizeParams ? "none" : "box-shadow 0.2s",
        position: "relative",
      }}
      onMouseEnter={() => {
        if (!resizeParams) setHovered(true);
      }}
      onMouseLeave={() => {
        if (!resizeParams) setHovered(false);
      }}
    >
      {editable && onDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="lumir-link-preview-delete"
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            border: "none",
            backgroundColor: "rgba(0,0,0,0.5)",
            color: "#fff",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            lineHeight: 1,
            zIndex: 2,
            opacity: 0,
            transition: "opacity 0.2s",
          }}
          title="삭제"
        >
          ✕
        </button>
      )}

      {editable && (hovered || resizeParams) && (
        <>
          <div
            className="lumir-resize-handle"
            style={{ left: "4px" }}
            onMouseDown={handleLeftDown}
          />
          <div
            className="lumir-resize-handle"
            style={{ right: "4px" }}
            onMouseDown={handleRightDown}
          />
        </>
      )}

      {image && !imgError && (
        <div
          style={{
            width: "100%",
            height: `${localHeight || 200}px`,
            overflow: "hidden",
            backgroundColor: "#f0f0f0",
            position: "relative",
          }}
        >
          <img
            src={image}
            alt={title || "Link preview"}
            onError={() => setImgError(true)}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
            referrerPolicy="no-referrer"
            loading="lazy"
          />
          {editable && (hovered || resizeParams) && (
            <div
              className="lumir-resize-handle-bottom"
              onMouseDown={handleBottomDown}
            />
          )}
        </div>
      )}

      <div style={{ padding: "12px 16px" }}>
        <div
          style={{
            fontSize: "14px",
            fontWeight: 600,
            color: "#1a1a1a",
            lineHeight: 1.4,
            marginBottom: description ? "4px" : "8px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title || domain}
        </div>

        {description && (
          <div
            style={{
              fontSize: "12px",
              color: "#666",
              lineHeight: 1.4,
              marginBottom: "8px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {description}
          </div>
        )}

        <div
          style={{
            fontSize: "12px",
            color: "#999",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {domain}
        </div>
      </div>
    </div>
  );
};

const LinkPreviewSkeleton = () => (
  <div
    className="lumir-link-preview-skeleton"
    style={{
      border: "1px solid #e0e0e0",
      borderRadius: "8px",
      overflow: "hidden",
      maxWidth: "400px",
      backgroundColor: "#fff",
    }}
  >
    <div
      style={{
        width: "100%",
        height: "200px",
        backgroundColor: "#f0f0f0",
        animation: "lumir-skeleton-pulse 1.5s ease-in-out infinite",
      }}
    />
    <div style={{ padding: "12px 16px" }}>
      <div
        style={{
          height: "16px",
          width: "70%",
          backgroundColor: "#f0f0f0",
          borderRadius: "4px",
          marginBottom: "8px",
          animation: "lumir-skeleton-pulse 1.5s ease-in-out infinite",
        }}
      />
      <div
        style={{
          height: "12px",
          width: "90%",
          backgroundColor: "#f0f0f0",
          borderRadius: "4px",
          marginBottom: "8px",
          animation: "lumir-skeleton-pulse 1.5s ease-in-out infinite",
        }}
      />
      <div
        style={{
          height: "12px",
          width: "40%",
          backgroundColor: "#f0f0f0",
          borderRadius: "4px",
          animation: "lumir-skeleton-pulse 1.5s ease-in-out infinite",
        }}
      />
    </div>
  </div>
);

const LinkPreviewError = ({
  url,
  onRetry,
}: {
  url: string;
  onRetry?: () => void;
}) => {
  const domain = (() => {
    try { return new URL(url).hostname; } catch { return url; }
  })();

  return (
    <div
      className="lumir-link-preview-error"
      style={{
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        overflow: "hidden",
        maxWidth: "400px",
        backgroundColor: "#fff",
        cursor: "pointer",
        position: "relative",
      }}
      onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
    >
      <div
        style={{
          width: "100%",
          height: "160px",
          overflow: "hidden",
          backgroundColor: "#f0f0f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={DEFAULT_LINK_PREVIEW_IMAGE}
          alt="Lumir"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
            padding: "20px",
            boxSizing: "border-box",
          }}
        />
      </div>
      <div style={{ padding: "12px 16px" }}>
        <div
          style={{
            fontSize: "13px",
            color: "#999",
            marginBottom: "4px",
          }}
        >
          미리보기를 불러올 수 없습니다
        </div>
        <div
          style={{
            fontSize: "12px",
            color: "#1a73e8",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {domain}
        </div>
      </div>
      {onRetry && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRetry(); }}
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            background: "rgba(0,0,0,0.5)",
            border: "none",
            borderRadius: "4px",
            padding: "4px 8px",
            cursor: "pointer",
            fontSize: "12px",
            color: "#fff",
            whiteSpace: "nowrap",
          }}
        >
          재시도
        </button>
      )}
    </div>
  );
};

const LinkPreviewUrlInput = ({
  onSubmit,
}: {
  onSubmit: (url: string) => void;
}) => {
  const [inputUrl, setInputUrl] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = useCallback(() => {
    const trimmed = inputUrl.trim();
    if (!trimmed) return;
    const normalized = /^https?:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;
    onSubmit(normalized);
  }, [inputUrl, onSubmit]);

  return (
    <div
      className="lumir-link-preview-input"
      style={{
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "16px",
        maxWidth: "400px",
        backgroundColor: "#fafafa",
        display: "flex",
        alignItems: "center",
        gap: "8px",
      }}
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#999"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flexShrink: 0 }}
      >
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
      <input
        ref={inputRef}
        type="text"
        value={inputUrl}
        onChange={(e) => setInputUrl(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleSubmit();
          }
        }}
        placeholder="URL을 입력하세요 (예: https://example.com)"
        style={{
          flex: 1,
          border: "none",
          outline: "none",
          backgroundColor: "transparent",
          fontSize: "13px",
          color: "#333",
          minWidth: 0,
        }}
      />
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!inputUrl.trim()}
        style={{
          flexShrink: 0,
          padding: "4px 12px",
          border: "none",
          borderRadius: "4px",
          backgroundColor: inputUrl.trim() ? "#3b82f6" : "#d1d5db",
          color: "#fff",
          fontSize: "12px",
          fontWeight: 500,
          cursor: inputUrl.trim() ? "pointer" : "not-allowed",
          transition: "background-color 0.15s",
        }}
      >
        확인
      </button>
    </div>
  );
};

export const LinkPreviewBlock = createReactBlockSpec(
  {
    type: "linkPreview",
    propSchema: {
      url: { default: "" },
      title: { default: "" },
      description: { default: "" },
      image: { default: "" },
      domain: { default: "" },
      previewWidth: { default: 400 as const },
      previewHeight: { default: 200 as const },
    },
    content: "none",
  },
  {
    render: (props) => {
      const { url, title, description, image, domain } =
        props.block.props;
      const [status, setStatus] = useState<"idle" | "loading" | "error">(
        !url ? "idle" : title ? "idle" : "loading"
      );
      const fetchedRef = useRef(false);

      const editable = props.editor.isEditable;

      useEffect(() => {
        if (!url || title || fetchedRef.current) {
          if (!url) setStatus("idle");
          return;
        }

        const getApiEndpoint = () =>
          (props.editor as any)._linkPreviewApiEndpoint as
            | string
            | undefined;

        const doFetch = (endpoint: string) => {
          fetchedRef.current = true;
          setStatus("loading");

          fetchLinkMetadata(url, endpoint)
            .then((metadata) => {
              props.editor.updateBlock(props.block, {
                props: {
                  title: metadata.title || "",
                  description: metadata.description || "",
                  image: metadata.image || "",
                  domain: metadata.domain || extractDomain(url),
                },
              });
              setStatus("idle");
            })
            .catch(() => {
              props.editor.updateBlock(props.block, {
                props: { domain: extractDomain(url) },
              });
              setStatus("error");
            });
        };

        const endpoint = getApiEndpoint();
        if (endpoint) {
          doFetch(endpoint);
        } else {
          // endpoint가 아직 설정되지 않았을 수 있으므로 잠시 대기 후 재시도
          const timer = setTimeout(() => {
            const retryEndpoint = getApiEndpoint();
            if (retryEndpoint) {
              doFetch(retryEndpoint);
            } else {
              setStatus("error");
            }
          }, 100);
          return () => clearTimeout(timer);
        }
      }, [url, title]);

      const handleRetry = useCallback(() => {
        const endpoint = (props.editor as any)._linkPreviewApiEndpoint as
          | string
          | undefined;
        if (!url || !endpoint) return;
        metadataCache.delete(url);
        fetchedRef.current = false;
        setStatus("loading");

        fetchLinkMetadata(url, endpoint)
          .then((metadata) => {
            props.editor.updateBlock(props.block, {
              props: {
                title: metadata.title || "",
                description: metadata.description || "",
                image: metadata.image || "",
                domain: metadata.domain || extractDomain(url),
              },
            });
            setStatus("idle");
          })
          .catch(() => {
            setStatus("error");
          });
      }, [url, props.editor, props.block]);

      const handleDelete = useCallback(() => {
        props.editor.removeBlocks([props.block]);
      }, [props.editor, props.block]);

      if (!url) {
        if (!editable) {
          return null;
        }
        return (
          <LinkPreviewUrlInput
            onSubmit={(newUrl) => {
              props.editor.updateBlock(props.block, {
                props: { url: newUrl },
              });
            }}
          />
        );
      }

      if (status === "loading") {
        return <LinkPreviewSkeleton />;
      }

      if (status === "error") {
        return <LinkPreviewError url={url} onRetry={handleRetry} />;
      }

      return (
        <LinkPreviewCard
          url={url}
          title={title}
          description={description}
          image={image}
          domain={domain || extractDomain(url)}
          editable={editable}
          onDelete={handleDelete}
          width={props.block.props.previewWidth}
          onWidthChange={(newWidth) => {
            props.editor.updateBlock(props.block, {
              props: { previewWidth: newWidth },
            });
          }}
          height={props.block.props.previewHeight}
          onHeightChange={(newHeight) => {
            props.editor.updateBlock(props.block, {
              props: { previewHeight: newHeight },
            });
          }}
        />
      );
    },
  }
);
